import { useMemo, useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, trackEvent } from '@/services/firebase';
import { Container, Section } from '@components';
import { Send, CheckCircle2, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { buildLeadSource } from '@/utils/leadSource';
import usePageMeta from '@/utils/usePageMeta';

const WHAT_I_DO = [
  {
    title: 'AI content & agent pipelines',
    text: 'Closed-loop systems that generate, publish, measure, and improve content automatically — like the engine running this site.',
  },
  {
    title: 'Workflow automation',
    text: 'Replace manual, repetitive processes with reliable automations on Google Cloud, Firebase, and the APIs you already use.',
  },
  {
    title: 'Data engineering & analytics',
    text: 'Pipelines, dashboards, and reporting that turn scattered data into decisions.',
  },
];

const WorkWithMe = () => {
  usePageMeta({
    title: 'Work With Us — Data & AI Consulting | DataLogicHub',
    description:
      'Hire DataLogicHub for data engineering, AI automation, dashboards, and reporting. Tell me about your project.',
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Capture once on first render — navigating within the SPA later must not
  // overwrite the attribution the visitor arrived with.
  const source = useMemo(
    () => buildLeadSource(window.location.search, document.referrer),
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in your name, email, and a short message.');
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'consulting_leads'), {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        source,
        createdAt: serverTimestamp(),
      });
      // GA4 recommended conversion event — the weekly goal doc tracks this
      trackEvent('generate_lead', {
        lead_source: source.summary,
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Lead submission failed:', err);
      toast.error('Something went wrong sending your message — please email me directly instead.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-neutral-soft-gray min-h-screen">
      <Section>
        <Container>
          <div className="max-w-3xl mx-auto">
            <h1 className="text-h2 text-primary-navy mb-3">Work with us</h1>
            <p className="text-body text-neutral-medium-gray mb-10">
              I&apos;m Byamba Enkhbat — a Chicago-based, twice Databricks-certified data
              engineer (
              <a
                href="https://credentials.databricks.com/056070f5-1581-4e24-8996-82b1f1e23b20#acc.3C6pjHkb"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                Associate
              </a>
              {' + '}
              <a
                href="https://credentials.databricks.com/d9114e66-f556-4da1-a383-fc65cb512027#acc.8lQgipFn"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                Professional
              </a>
              , both verifiable) working hands-on with Spark, GCP, and LLM systems. I take
              on consulting and freelance engagements — from a quick automation that saves
              your team hours a week, to full AI content and data pipelines built on your
              stack.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
              {WHAT_I_DO.map((item) => (
                <div key={item.title} className="bg-white rounded-2xl shadow-sm p-5">
                  <h3 className="text-sm font-semibold text-primary-navy mb-2">{item.title}</h3>
                  <p className="text-xs text-neutral-medium-gray leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            {submitted ? (
              <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={40} />
                <h2 className="text-lg font-semibold text-gray-800 mb-1">Message received</h2>
                <p className="text-sm text-neutral-medium-gray">
                  Thanks — I read every inquiry personally and will get back to you within a
                  day or two.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 space-y-5">
                <h2 className="text-lg font-semibold text-gray-800">Tell me about your project</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="lead-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={200}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-teal"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lead-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="lead-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={320}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-teal"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="lead-message" className="block text-sm font-medium text-gray-700 mb-1.5">
                    What do you want to build or automate? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="lead-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    maxLength={5000}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-teal resize-none"
                    placeholder="A couple of sentences about the problem, your current stack, and rough timeline."
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <a
                    href="mailto:zolbayar@datalogichub.com"
                    className="flex items-center gap-1.5 text-sm text-neutral-medium-gray hover:text-primary-teal transition-colors"
                  >
                    <Mail size={14} /> Prefer email? zolbayar@datalogichub.com
                  </a>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center gap-2 px-6 py-2.5 bg-primary-navy text-white text-sm font-semibold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send size={15} /> Send inquiry
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Container>
      </Section>
    </div>
  );
};

export default WorkWithMe;
